import { SEMANTIC_COLORS } from '../nodes/nodeChartTheme';

/**
 * Средний ориентир качества: сравнение балла сотрудника со средним по всем сотрудникам.
 *
 * Сервер отдаёт сырые числа (`GET /analytics/employees/quality-comparison`), а округление,
 * отклонение и «выше / на уровне / ниже» живут здесь — потому что порог обязан применяться
 * к той же разнице, что видна на подписи. Иначе при сыром +0,21 подпись «+0,2» значилась бы
 * «выше среднего», хотя порог «на уровне» — ±0,2.
 *
 * Шкала балла — 0..10, как у плитки «Средний чек-лист» и «Динамики качества работы».
 */

/** «На уровне среднего» — отклонение по модулю не больше этого числа баллов. */
export const QUALITY_LEVEL_TOLERANCE = 0.2;

const TOLERANCE_TENTHS = Math.round(QUALITY_LEVEL_TOLERANCE * 10);

/** U+2212: дефис рядом с плюсом читается хуже и короче настоящего минуса. */
const MINUS = '−';

const readFinite = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : null);

const formatTenths = (tenths) =>
  (Math.abs(tenths) / 10).toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** Балл в десятых долях — в той точности, в какой он показан. */
export const toTenths = (value) => {
  const number = readFinite(value);
  return number === null ? null : Math.round(number * 10);
};

/** «7,9»; нет балла — «—». */
export const formatScore = (value) => {
  const tenths = toTenths(value);
  return tenths === null ? '—' : formatTenths(tenths);
};

/** Отклонение в десятых долях: «+0,5», «−0,3», «0,0». */
export const formatDeviation = (deltaTenths) => {
  if (!Number.isFinite(deltaTenths)) {
    return '—';
  }

  if (deltaTenths === 0) {
    return formatTenths(0);
  }

  return `${deltaTenths > 0 ? '+' : MINUS}${formatTenths(deltaTenths)}`;
};

/** Разница с ориентиром в десятых долях: обе величины округляются так же, как на подписях. */
export const deviationTenths = (score, average) => {
  const scoreTenths = toTenths(score);
  const averageTenths = toTenths(average);
  return scoreTenths === null || averageTenths === null ? null : scoreTenths - averageTenths;
};

/** `'above' | 'level' | 'below'`; без балла или без среднего — `null`. */
export const classifyDeviation = (score, average) => {
  const delta = deviationTenths(score, average);
  if (delta === null) {
    return null;
  }

  if (delta > TOLERANCE_TENTHS) {
    return 'above';
  }

  return delta < -TOLERANCE_TENTHS ? 'below' : 'level';
};

/**
 * Положение относительно среднего. Зелёный и красный под дейтеранопией неразличимы, поэтому
 * цвет никогда не работает один: на столбце знак отклонения, в легенде и тултипе — значок
 * и слово.
 */
export const DEVIATION_LEVELS = {
  above: { label: 'выше среднего', symbol: '▲', color: SEMANTIC_COLORS.positive },
  level: { label: 'на уровне', symbol: '=', color: SEMANTIC_COLORS.neutral },
  below: { label: 'ниже среднего', symbol: '▼', color: SEMANTIC_COLORS.negative }
};

export const DEVIATION_LEVEL_ORDER = ['above', 'level', 'below'];

/** «±0,2» — для легенды. */
export const TOLERANCE_LABEL = `±${formatTenths(TOLERANCE_TENTHS)}`;

const readName = (value) => (typeof value === 'string' && value.trim() ? value.trim() : '—');

/**
 * Защитное чтение ответа. Вместо объекта может прийти что угодно, вплоть до функции-заглушки
 * смоук-теста, — тогда сравнение пустое, а не исключение в рендере.
 */
export const readQualityComparison = (response) => {
  const source = response !== null && typeof response === 'object' ? response : {};
  const rows = Array.isArray(source.rows) ? source.rows : [];

  return {
    average: readFinite(source.networkAverage),
    scoredCalls: readFinite(source.networkScoredCalls) ?? 0,
    employees: readFinite(source.networkEmployees) ?? 0,
    rows: rows
      .filter((row) => row !== null && typeof row === 'object')
      .map((row) => ({
        id: String(row.employeeId ?? row.employeeName ?? ''),
        name: readName(row.employeeName),
        score: readFinite(row.avgScore),
        scoredCalls: readFinite(row.scoredCalls) ?? 0
      }))
      .filter((row) => row.score !== null)
  };
};

const EMPTY_COMPARISON = readQualityComparison(null);

const byScoreThenName = (left, right) =>
  right.score - left.score || left.name.localeCompare(right.name, 'ru', { sensitivity: 'base' });

/**
 * Столбцы графика: по убыванию балла, с положением, цветом и готовой подписью. Подпись
 * кладётся в данные строкой — `LabelList` отдаёт в отрисовку только `value`.
 *
 * `compact` — телефон: подпись сокращается до отклонения, сам балл читается по оси.
 */
export const buildComparisonBars = (comparison = EMPTY_COMPARISON, { compact = false } = {}) => {
  const { average, rows } = comparison ?? EMPTY_COMPARISON;

  return [...rows].sort(byScoreThenName).map((row) => {
    const level = classifyDeviation(row.score, average);
    const delta = deviationTenths(row.score, average);
    const scoreLabel = formatScore(row.score);
    const deltaLabel = delta === null ? null : formatDeviation(delta);

    let valueLabel = scoreLabel;
    if (deltaLabel !== null) {
      valueLabel = compact ? deltaLabel : `${scoreLabel} · ${deltaLabel}`;
    }

    return {
      key: row.id,
      label: row.name,
      score: row.score,
      scoredCalls: row.scoredCalls,
      level,
      color: level ? DEVIATION_LEVELS[level].color : SEMANTIC_COLORS.progress,
      scoreLabel,
      deltaLabel,
      valueLabel
    };
  });
};

const FILTER_NOTE = ' — без учёта фильтра сотрудников и отделов';

/**
 * Подпись ориентира: «Среднее по всем сотрудникам: 7,4 (оценённых звонков: 128)». При
 * выбранных сотрудниках или отделах честно говорит, что фильтр на среднее не действует.
 */
export const buildAverageCaption = (comparison = EMPTY_COMPARISON, { filtered = false } = {}) => {
  const { average, scoredCalls } = comparison ?? EMPTY_COMPARISON;
  if (average === null) {
    return null;
  }

  const calls = scoredCalls.toLocaleString('ru-RU');
  return `Среднее по всем сотрудникам: ${formatScore(average)} (оценённых звонков: ${calls})${filtered ? FILTER_NOTE : ''}`;
};

/** Горизонталь среднего для «Динамики качества работы»; среднего нет — линии нет. */
export const buildTrendReference = (comparison = EMPTY_COMPARISON, { filtered = false } = {}) => {
  const { average } = comparison ?? EMPTY_COMPARISON;
  if (average === null) {
    return null;
  }

  return {
    value: average,
    caption: `Пунктир — среднее по всем сотрудникам за период: ${formatScore(average)}${filtered ? FILTER_NOTE : ''}`
  };
};

/**
 * Какие запросы ориентира нужны: у сравнения своя форма, линия на динамике следует форме
 * динамики. Одинаковые формы — один запрос на оба графика, скрытая панель не запрашивает
 * ничего.
 *
 * @returns {{ form: string, targets: ('comparison' | 'trend')[] }[]}
 */
export const planBenchmarkRequests = ({
  comparisonVisible = false,
  comparisonForm = 'all',
  trendVisible = false,
  trendForm = 'all'
} = {}) => {
  const plan = [];
  const add = (form, target) => {
    const existing = plan.find((entry) => entry.form === form);
    if (existing) {
      existing.targets.push(target);
    } else {
      plan.push({ form, targets: [target] });
    }
  };

  if (comparisonVisible) {
    add(comparisonForm || 'all', 'comparison');
  }

  if (trendVisible) {
    add(trendForm || 'all', 'trend');
  }

  return plan;
};
